import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { TopUpInvoice } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon } from '../ChatAssistant/compact/kit';
import { MIN_TOP_UP, SUPPORT_TELEGRAM_URL, usd, usdShort } from './model';
import { Caption, GhostBtn, Modal, ModalFoot, ModalHead, Note, PrimaryBtn, TextBtn } from './ui';

const PRESETS = [75, 150, 300, 600];
type Cur = 'USDT' | 'USDC';
const CURS: { id: Cur; sym: string; net: string; tag: string }[] = [
  { id: 'USDT', sym: 'T', net: 'TRON · TRC20', tag: 'TRC20' },
  { id: 'USDC', sym: 'C', net: 'Ethereum · ERC20', tag: 'ERC20' },
];

export function TopUpDialog({ wallet, initialAmount, initialCode, onClose, onDone }: { wallet: number; initialAmount?: number; initialCode?: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('paymentsCompact');
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [preset, setPreset] = useState(75);
  const [custom, setCustom] = useState(initialAmount ? String(Math.max(MIN_TOP_UP, initialAmount)) : '');
  const [code, setCode] = useState((initialCode || '').toUpperCase());
  const [bonus, setBonus] = useState<{ ok: boolean; is_referral: boolean; bonus_cents: number; reason?: string } | null>(null);
  const [cur, setCur] = useState<Cur>('USDT');
  const [invoice, setInvoice] = useState<TopUpInvoice | null>(null);
  const [ttl, setTtl] = useState(1);
  const [now, setNow] = useState(Date.now());
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [copiedAmt, setCopiedAmt] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);

  const customN = parseFloat(custom);
  const amount = custom ? (Number.isFinite(customN) ? customN : 0) : preset;
  const tooLow = amount < MIN_TOP_UP;

  // Live referral bonus preview, debounced.
  useEffect(() => {
    const c = code.trim();
    if (!c || tooLow) { setBonus(null); return; }
    let alive = true;
    const h = setTimeout(() => {
      api.previewTopUpReferral(Math.round(amount * 100), c).then((r) => alive && setBonus(r)).catch(() => alive && setBonus(null));
    }, 400);
    return () => { alive = false; clearTimeout(h); };
  }, [code, amount, tooLow]);

  useEffect(() => {
    if (step !== 3) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [step]);

  const generate = async () => {
    setCreating(true);
    setErr('');
    try {
      const inv = await api.createTopUpInvoice(Math.round(amount * 100), cur, code.trim() ? { code: code.trim() } : undefined);
      setInvoice(inv);
      setTtl(Math.max(1, (new Date(inv.expires_at).getTime() - Date.now()) / 1000));
      setNow(Date.now());
      setCopiedAmt(false);
      setCopiedAddr(false);
      setStep(3);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('top.invoiceError'));
    } finally {
      setCreating(false);
    }
  };

  const meta = CURS.find((c) => c.id === cur)!;
  // The amount to send is rounded up to a whole coin so it's easy to type in a wallet app.
  const sendAmount = String(Math.ceil(invoice?.pay_amount ?? amount));
  const sendSym = (invoice?.pay_currency || cur).toUpperCase().replace(/TRC20|ERC20/, '').trim() || cur;
  const left = invoice ? Math.max(0, Math.round((new Date(invoice.expires_at).getTime() - now) / 1000)) : 0;
  // Tron gateway (ADR 0046): the address is the User's own and any amount is credited.
  const anyAmount = invoice?.provider === 'tron';
  const timer = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');

  const copy = (text: string, done: (v: boolean) => void) => {
    navigator.clipboard.writeText(text).catch(() => {});
    done(true);
    setTimeout(() => done(false), 1600);
  };

  const close = () => { if (step === 4) onDone(); onClose(); };

  return (
    <Modal width={460} onClose={close} closeOnBackdrop={step !== 3}>
      <ModalHead
        icon="credit-card"
        title={t('top.title')}
        pad="14px 16px 0"
        onClose={close}
        right={<span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{t('top.balance', { amount: usd(wallet) })}</span>}
      />
      {step < 4 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px 2px' }}>
          {(['amount', 'currency', 'payment'] as const).map((k, i) => {
            const n = i + 1, done = n < step, here = n === step;
            return (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: i ? '1' : '0 0 auto' }}>
                {i > 0 && <span style={{ flex: 1, minWidth: 12, height: 1, background: n <= step ? C.purple : C.borderHi }} />}
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 20, height: 20, boxSizing: 'border-box', borderRadius: 99, border: `1px solid ${done || here ? C.purple : C.borderHi}`, background: here ? C.purple : done ? 'color-mix(in srgb, var(--om-accent) 18%, transparent)' : 'transparent', color: here ? '#fff' : done ? C.purpleLt : C.muted, font: f(600, 11), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {done ? <Icon name="check" size={11} /> : n}
                  </span>
                  <span style={{ font: f(500, 12), color: here ? C.text : done ? C.purpleLt : C.muted, whiteSpace: 'nowrap' }}>{t(`top.step.${k}`)}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {step === 1 && (
        <>
          <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ font: f(600, 13), color: C.text }}>{t('top.amount')}</span>
                <span style={{ font: f(400, 11.5), color: C.muted }}>{t('top.minimum', { amount: usd(MIN_TOP_UP, 0) })}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 6 }}>
                {PRESETS.map((v) => {
                  const on = !custom && preset === v;
                  return (
                    <button key={v} type="button" onClick={() => { setPreset(v); setCustom(''); }} className="pay-card" style={{ height: 36, border: `1px solid ${on ? C.purple : C.borderHi}`, borderRadius: 7, background: on ? C.tint16 : 'transparent', color: on ? C.purplePale : 'var(--om-body)', font: f(600, 13.5), cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>
                      {usd(v, 0)}
                    </button>
                  );
                })}
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 11, top: 9, font: f(500, 13), color: C.muted }}>$</span>
                <input
                  value={custom}
                  inputMode="decimal"
                  onChange={(e) => {
                    const v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                    const [a, ...rest] = v.split('.');
                    setCustom(rest.length ? `${a}.${rest.join('')}` : v);
                  }}
                  placeholder={t('top.custom')}
                  style={{ width: '100%', height: 36, boxSizing: 'border-box', padding: '0 11px 0 23px', background: C.input, border: `1px solid ${custom && tooLow ? C.purple : custom ? 'var(--om-accent-deep)' : C.borderHi}`, borderRadius: 7, color: C.text, font: f(500, 13), outline: 'none' }}
                />
              </div>
              {custom && tooLow && <Note icon="alert-circle" tone="accent">{t('top.tooLow', { amount: usd(MIN_TOP_UP, 0) })}</Note>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ font: f(600, 13), color: C.text }}>{t('top.code')}</span>
                <span style={{ font: f(400, 11.5), color: C.muted }}>{t('top.optional')}</span>
              </div>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                spellCheck={false}
                autoComplete="off"
                placeholder="CODE"
                style={{ width: '100%', height: 36, boxSizing: 'border-box', padding: '0 11px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 7, color: C.text, font: '500 13px var(--font-mono)', letterSpacing: '.08em', outline: 'none' }}
              />
              <span style={{ font: f(400, 11.5), color: bonus?.ok && bonus.bonus_cents > 0 ? C.purplePale : C.muted }}>
                {bonus?.ok && bonus.bonus_cents > 0
                  ? t('top.bonus', { amount: usd(bonus.bonus_cents / 100) })
                  : bonus && bonus.is_referral && !bonus.ok
                    ? bonus.reason
                    : t('top.codeHint')}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.input }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, font: f(400, 12.5), color: C.sub }}>{t('top.adding')}</span>
                <span style={{ font: f(700, 16), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{usdShort(amount)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, font: f(400, 12), color: C.muted }}>{t('top.newBalance')}</span>
                <span style={{ font: f(500, 12), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{usd(wallet + amount)}</span>
              </div>
            </div>
            <Note icon="lock">
              <Trans
                t={t}
                i18nKey="top.nonRefundable"
                components={{ a: <a href={SUPPORT_TELEGRAM_URL} target="_blank" rel="noreferrer" style={{ color: C.purpleLt, fontWeight: 500 }} /> }}
              />
            </Note>
          </div>
          <ModalFoot>
            <span style={{ flex: 1 }} />
            <TextBtn onClick={close}>{t('cancel')}</TextBtn>
            <PrimaryBtn iconRight="arrow-right" disabled={tooLow} onClick={() => !tooLow && setStep(2)}>{t('top.continue')}</PrimaryBtn>
          </ModalFoot>
        </>
      )}

      {step === 2 && (
        <>
          <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.input }}>
              <span style={{ flex: 1, font: f(400, 12.5), color: C.sub }}>{t('top.toPay')}</span>
              <span style={{ font: f(700, 16), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{usdShort(amount)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ font: f(600, 13), color: C.text }}>{t('top.payWith')}</span>
                <span style={{ font: f(400, 11.5), color: C.muted }}>{t('top.payWithHint')}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
                {CURS.map((c) => {
                  const on = cur === c.id;
                  return (
                    <button key={c.id} type="button" onClick={() => setCur(c.id)} className="pay-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: `1px solid ${on ? C.purple : C.borderHi}`, borderRadius: 8, background: on ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : C.input, cursor: 'pointer', textAlign: 'left' }}>
                      <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 99, background: C.segActive, color: C.purplePale, font: f(700, 12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.sym}</span>
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ font: f(600, 14), color: C.text }}>{c.id}</span>
                        <span style={{ font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>{c.net}</span>
                      </span>
                      <span style={{ width: 16, height: 16, flex: 'none', boxSizing: 'border-box', borderRadius: 99, border: `1px solid ${on ? C.purple : C.faint}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: on ? C.purple : 'transparent' }} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <Note>{t('top.networkWarn')}</Note>
            {err && <Note icon="alert-circle" tone="accent">{err}</Note>}
          </div>
          <ModalFoot>
            <GhostBtn icon="arrow-left" height={32} onClick={() => setStep(1)} style={{ font: f(500, 12.5), borderRadius: 7 }}>{t('top.back')}</GhostBtn>
            <span style={{ flex: 1 }} />
            <PrimaryBtn iconRight="arrow-right" disabled={creating} onClick={() => void generate()}>{creating ? t('top.generating') : t('top.generate')}</PrimaryBtn>
          </ModalFoot>
        </>
      )}

      {step === 3 && invoice && (
        <>
          <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 12, border: '1px solid var(--om-accent-deep)', borderRadius: 8, background: 'color-mix(in srgb, var(--om-accent) 10%, transparent)' }}>
              <Caption style={{ color: C.sub }}>{anyAmount ? t('top.sendAmount') : t('top.sendExactly')}</Caption>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ font: f(700, 20), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{sendAmount}</span>
                <span style={{ font: f(600, 13), color: C.purplePale }}>{sendSym}</span>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={() => copy(sendAmount, setCopiedAmt)} className="cpt-hover" style={{ height: 26, padding: '0 9px', display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: '1px solid var(--om-accent-deep)', borderRadius: 6, color: C.purplePale, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <Icon name={copiedAmt ? 'check' : 'copy'} size={12} />
                  {copiedAmt ? t('copied') : t('copy')}
                </button>
              </div>
              <span style={{ font: f(400, 11.5), color: C.sub }}>{t('top.onNetwork', { net: meta.net })}</span>
              {anyAmount && <span style={{ font: f(400, 11.5, 1.45), color: C.sub }}>{t('top.anyAmount')}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 132, height: 132, flex: 'none', boxSizing: 'border-box', padding: 8, background: '#fff', borderRadius: 8 }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=232x232&margin=0&data=${encodeURIComponent(invoice.qr_payload || invoice.wallet_address)}`}
                  alt={t('top.qr')}
                  style={{ width: '100%', height: '100%', display: 'block' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Caption>{t('top.address', { tag: meta.tag })}</Caption>
                <span style={{ font: '500 12px/1.5 var(--font-mono)', color: C.text, wordBreak: 'break-all' }}>{invoice.wallet_address}</span>
                <GhostBtn icon={copiedAddr ? 'check' : 'copy'} height={28} onClick={() => copy(invoice.wallet_address, setCopiedAddr)} style={{ alignSelf: 'flex-start' }}>
                  {copiedAddr ? t('copied') : t('top.copyAddress')}
                </GhostBtn>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, font: f(400, 12), color: C.sub }}>{left > 0 ? t('top.expiresIn') : anyAmount ? t('top.expiredTron') : t('top.expired')}</span>
                <span style={{ font: f(600, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{timer}</span>
              </div>
              <div style={{ height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: Math.min(100, (left / ttl) * 100) + '%', background: C.purple, borderRadius: 99, transition: 'width 1s linear' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.input }}>
              <Icon name="info" size={14} color={C.purpleLt} style={{ marginTop: 2 }} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ font: f(500, 12.5, 1.45), color: C.text }}>{t('top.wontClose')}</span>
                <span style={{ font: f(400, 12, 1.5), color: C.sub }}>
                  <Trans
                    t={t}
                    i18nKey="top.arrives"
                    components={{ a: <a href={SUPPORT_TELEGRAM_URL} target="_blank" rel="noreferrer" style={{ color: C.purpleLt, fontWeight: 500 }} /> }}
                  />
                </span>
              </span>
            </div>
          </div>
          <ModalFoot>
            <GhostBtn icon="arrow-left" height={32} onClick={() => setStep(2)} style={{ font: f(500, 12.5), borderRadius: 7 }}>{t('top.changeCurrency')}</GhostBtn>
            <span style={{ flex: 1 }} />
            <PrimaryBtn icon="check" onClick={() => setStep(4)}>{t('top.sent')}</PrimaryBtn>
          </ModalFoot>
        </>
      )}

      {step === 4 && (
        <>
          <div style={{ padding: '28px 16px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
            <span style={{ width: 44, height: 44, borderRadius: 99, background: 'color-mix(in srgb, var(--om-accent) 18%, transparent)', color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check" size={22} />
            </span>
            <span style={{ font: f(700, 18), color: C.text }}>{t('top.doneTitle', { amount: usdShort(amount) })}</span>
            <span style={{ font: f(400, 12.5, 1.5), color: C.sub, maxWidth: 360 }}>{t('top.doneBody', { sym: sendSym, tag: meta.tag })}</span>
          </div>
          <ModalFoot>
            <span style={{ flex: 1 }} />
            <PrimaryBtn onClick={close}>{t('top.done')}</PrimaryBtn>
          </ModalFoot>
        </>
      )}
    </Modal>
  );
}
